/**
 * Product View Page
 * =================
 * View product details (read-only).
 * Click Edit to modify the product.
 */

import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Edit, Package, MapPin, FolderTree, Factory, Trash2, Plus, Minus, Tag } from 'lucide-react'
import { Card, CardContent, CardHeader } from '@/components/Card'
import Button from '@/components/Button'
import ImageGallery from '@/components/ImageGallery'
import { useProduct, useDeleteProduct, useUpdateProduct } from '@/hooks/useProducts'
import { PRODUCT_STATUS_LABELS } from '@/types'

export default function ProductView() {
  const navigate = useNavigate()
  const { productId } = useParams<{ productId: string }>()
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [adjustQuantity, setAdjustQuantity] = useState('')
  const [isAdjusting, setIsAdjusting] = useState(false)

  const { data: productData, isLoading } = useProduct(productId ?? '')
  const deleteProduct = useDeleteProduct()
  const updateProduct = useUpdateProduct()
  const product = productData?.data

  const handleDelete = async () => {
    if (!product) return
    try {
      await deleteProduct.mutateAsync(product.id)
      navigate('/products')
    } catch (error) {
      console.error('Failed to delete product:', error)
    }
  }

  const handleAdjustQuantity = async () => {
    if (!product || !adjustQuantity) return
    const adjustment = parseInt(adjustQuantity, 10)
    if (isNaN(adjustment)) return

    const newQuantity = Math.max(0, product.quantity + adjustment)
    setIsAdjusting(true)
    try {
      await updateProduct.mutateAsync({
        id: product.id,
        data: { quantity: newQuantity }
      })
      setAdjustQuantity('')
    } catch (error) {
      console.error('Failed to adjust quantity:', error)
    } finally {
      setIsAdjusting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">Loading product...</p>
      </div>
    )
  }

  if (!product) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 dark:text-red-400">Product not found</p>
        <Button variant="secondary" onClick={() => navigate('/products')} className="mt-4">
          Back to Products
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/products')}>
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{product.name}</h1>
            <p className="text-gray-500 dark:text-gray-400">{product.product_id}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            icon={<Plus size={16} />}
            onClick={() => navigate('/products/new')}
          >
            Add Product
          </Button>
          <Button
            icon={<Edit size={16} />}
            onClick={() => navigate(`/products/${product.product_id}/edit`)}
          >
            Edit Product
          </Button>
          <Button
            variant="danger"
            icon={<Trash2 size={16} />}
            onClick={() => setShowDeleteConfirm(true)}
          >
            Delete
          </Button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Delete Product?
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Are you sure you want to delete "{product.name}"? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleDelete}
                loading={deleteProduct.isPending}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Product Information */}
          <Card>
            <CardHeader title="Product Information" />
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Product Name
                </label>
                <p className="text-gray-900 dark:text-white">{product.name}</p>
              </div>

              {product.description && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Description
                  </label>
                  <p className="text-gray-900 dark:text-white whitespace-pre-wrap">{product.description}</p>
                </div>
              )}

              {product.details && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Product Details
                  </label>
                  <p className="text-gray-900 dark:text-white whitespace-pre-wrap">{product.details}</p>
                </div>
              )}

              {product.compatible_products && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Compatible Products
                  </label>
                  <p className="text-gray-900 dark:text-white whitespace-pre-wrap">{product.compatible_products}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    SKU / Part Number
                  </label>
                  <p className="text-gray-900 dark:text-white font-mono">{product.sku || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Barcode (UPC/EAN)
                  </label>
                  <p className="text-gray-900 dark:text-white font-mono">{product.barcode || '-'}</p>
                </div>
              </div>

              {product.service_tag && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Service Tag
                  </label>
                  <p className="text-gray-900 dark:text-white font-mono">{product.service_tag}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pricing */}
          <Card>
            <CardHeader title="Pricing" />
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Cost Price
                  </label>
                  <p className="text-gray-900 dark:text-white">
                    {product.cost_price ? `$${product.cost_price}` : '-'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    Sale Price
                  </label>
                  <p className="text-gray-900 dark:text-white">
                    {product.sale_price ? `$${product.sale_price}` : '-'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Inventory */}
          <Card>
            <CardHeader title="Inventory" />
            <CardContent>
              <div className="text-center">
                <span
                  className={`inline-flex px-4 py-2 text-2xl font-bold rounded-lg ${
                    product.quantity === 0
                      ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-400'
                      : product.quantity < 5
                        ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-400'
                        : 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400'
                  }`}
                >
                  {product.quantity}
                </span>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">in stock</p>

                {/* Quick adjust quantity */}
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Quick Adjust</p>
                  <div className="flex items-center justify-center gap-2">
                    <div className="relative">
                      <input
                        type="number"
                        value={adjustQuantity}
                        onChange={(e) => setAdjustQuantity(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAdjustQuantity()}
                        placeholder="+/-"
                        className="w-20 px-3 py-1.5 text-center text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <button
                      onClick={handleAdjustQuantity}
                      disabled={!adjustQuantity || isAdjusting}
                      className={`p-1.5 rounded-lg transition-colors ${
                        adjustQuantity && parseInt(adjustQuantity, 10) > 0
                          ? 'bg-green-500 hover:bg-green-600 text-white'
                          : adjustQuantity && parseInt(adjustQuantity, 10) < 0
                            ? 'bg-red-500 hover:bg-red-600 text-white'
                            : 'bg-gray-200 dark:bg-gray-600 text-gray-400 dark:text-gray-500'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {adjustQuantity && parseInt(adjustQuantity, 10) < 0 ? (
                        <Minus size={18} />
                      ) : (
                        <Plus size={18} />
                      )}
                    </button>
                  </div>
                  {adjustQuantity && !isNaN(parseInt(adjustQuantity, 10)) && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      New total: <span className="font-medium">{Math.max(0, product.quantity + parseInt(adjustQuantity, 10))}</span>
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Organization */}
          <Card>
            <CardHeader title="Organization" />
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700">
                  <FolderTree className="text-gray-600 dark:text-gray-400" size={20} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
                    Category
                  </label>
                  <p className="text-gray-900 dark:text-white">{product.category?.name || 'Uncategorized'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700">
                  <MapPin className="text-gray-600 dark:text-gray-400" size={20} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
                    Location
                  </label>
                  <p className="text-gray-900 dark:text-white">{product.location?.name || 'No location'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {product.manufacturer?.logo_url ? (
                  <img
                    src={product.manufacturer.logo_url}
                    alt={product.manufacturer.name}
                    className="h-9 w-9 object-contain rounded-lg bg-gray-100 dark:bg-gray-700 p-1"
                    onError={(e) => {
                      // Fall back to icon if logo fails to load
                      const target = e.target as HTMLImageElement
                      target.style.display = 'none'
                      target.nextElementSibling?.classList.remove('hidden')
                    }}
                  />
                ) : null}
                <div className={`p-2 rounded-lg bg-gray-100 dark:bg-gray-700 ${product.manufacturer?.logo_url ? 'hidden' : ''}`}>
                  <Factory className="text-gray-600 dark:text-gray-400" size={20} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
                    Manufacturer
                  </label>
                  <p className="text-gray-900 dark:text-white">{product.manufacturer?.name || 'No manufacturer'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700">
                  <Tag className="text-gray-600 dark:text-gray-400" size={20} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
                    Status
                  </label>
                  <p className="text-gray-900 dark:text-white">{PRODUCT_STATUS_LABELS[product.status] || product.status}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Product Images */}
          <Card>
            <CardHeader title="Product Images" />
            <CardContent>
              {product.images && product.images.length > 0 ? (
                <ImageGallery
                  images={product.images}
                  productId={product.id}
                  editable={false}
                />
              ) : (
                <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                  <Package className="mx-auto h-8 w-8 mb-2 opacity-50" />
                  <p className="text-sm">No images</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Timestamps */}
          <Card>
            <CardContent className="text-sm text-gray-500 dark:text-gray-400 space-y-1">
              <p>
                Created: {new Date(product.created_at).toLocaleDateString()}
              </p>
              <p>
                Updated: {new Date(product.updated_at).toLocaleDateString()}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
